// Editor screen — ported from Claude Design/app/editor.jsx
// Adapted: react-router + real API; STATE.storySubs replaced with /api/clips/:id subtitles.

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { applyCaptionPreviewScale } from '../lib/captionRender.js';
import { normalizeSettings, previewLineStyle, previewBoxStyle, resolveEnPosY } from '../lib/captionSettings.js';
import { useClipPipeline, renderStatusLabel, publishStatusLabel } from '../lib/useClipPipeline.js';
import { useStore } from '../state/store.js';
import { Icons, findTopic, fmtClock, fmtDur, fmtT, parseClock, parseSubTime } from '../components/ui.jsx';

export function EditorScreen() {
  const { clipId } = useParams();
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);
  const topics = useStore((s) => s.topics);

  const [clip, setClip] = useState(null);
  const [episode, setEpisode] = useState(null);
  const [subs, setSubs] = useState([]);             // [{ id, t (relative to clip), d, text }]
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [caret, setCaret] = useState(null);
  const [editLang, setEditLang] = useState('es'); // 'es' | 'ru'

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1.0);
  const [playhead, setPlayhead] = useState(0);
  const [clipStart, setClipStart] = useState(0);
  const [clipEnd, setClipEnd] = useState(0);
  const clipDur = Math.max(1, clipEnd - clipStart);

  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState([]);
  const [postKinds, setPostKinds] = useState({ ig_reels: true, ig_stories: true, ig_feed: false, yt_shorts: false, telegram: false, vk_clip: false });
  const [postWhen, setPostWhen] = useState('sched');
  const [schedDate, setSchedDate] = useState(() => {
    const d = new Date(Date.now() + 24 * 3600e3);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [schedTime, setSchedTime] = useState('09:00');
  const [publishOverlay, setPublishOverlay] = useState(null); // null | 'sched' | 'now' | 'wait_render'
  const [videoError, setVideoError] = useState(null);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [generatingCaption, setGeneratingCaption] = useState(false);
  // IDs of rows whose Spanish text has been edited since load — their cached
  // English translation is stale, so we show the per-row "EN" button only on
  // these (instead of always showing it on every row).
  const [dirtyEs, setDirtyEs] = useState(new Set());
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let alive = true;
    api.getSettings().then((sRes) => {
      if (!alive) return;
      setSettings(normalizeSettings(sRes));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const videoRef = useRef(null);

  const clampRel = useCallback((rel) => Math.max(0, Math.min(clipDur, rel)), [clipDur]);

  // Keep episode video inside [clipStart, clipEnd] — preview is clip-only, not full episode.
  const enforceClipBounds = useCallback(() => {
    const v = videoRef.current;
    if (!v || !clipEnd) return;
    const abs = v.currentTime;
    const end = clipEnd;
    const start = clipStart;
    if (abs >= end - 0.04) {
      if (abs > end + 0.02) v.currentTime = end;
      if (!v.paused) v.pause();
      setPlayhead(clipDur);
      setPlaying(false);
      return;
    }
    if (abs < start - 0.04) {
      v.currentTime = start;
      setPlayhead(0);
      return;
    }
    setPlayhead(clampRel(abs - start));
  }, [clipStart, clipEnd, clipDur, clampRel]);

  // Seek video to a clip-relative time (in seconds). Resilient — silently
  // ignores if videoRef hasn't loaded yet.
  const seekTo = useCallback((rel) => {
    const r = clampRel(rel);
    if (!videoRef.current) return;
    const abs = clipStart + r;
    try {
      videoRef.current.currentTime = abs;
      setPlayhead(r);
    } catch {
      const tryAgain = () => {
        try { videoRef.current.currentTime = abs; setPlayhead(r); } catch {}
        videoRef.current.removeEventListener('canplay', tryAgain);
      };
      videoRef.current.addEventListener('canplay', tryAgain);
    }
  }, [clipStart, clampRel]);

  const [loadError, setLoadError] = useState(null);

  // Single source of truth for mapping a subtitle row from the API into the
  // local editor shape. Used by initial load AND after merge/split/refresh —
  // previously each call site duplicated this and merge/split forgot to copy
  // text_en + kind, which made EN translations vanish after edits.
  const mapSub = (s, baseStart) => {
    const rel = s.text_relative_start != null
      ? Number(s.text_relative_start)
      : Number(s.word_start_abs) - baseStart;
    const relEnd = s.text_relative_end != null
      ? Number(s.text_relative_end)
      : Number(s.word_end_abs) - baseStart;
    return {
      id: s.id,
      t: rel,
      d: Math.max(0.3, relEnd - rel),
      text: s.text || '',
      text_en: s.text_en || '',
      text_ru: s.text_ru || '',
      kind: s.kind || 'text',
    };
  };

  // Load clip + subtitles. Episode loads independently so the editor renders
  // as soon as the clip is available — no need to wait for the heavy
  // episode payload (chapters + nested clips + subtitle counts).
  useEffect(() => {
    let alive = true;
    setLoadError(null);
    (async () => {
      try {
        const c = await api.getClip(clipId);
        if (!alive) return;
        setClip(c);
        setClipStart(Number(c.t_start) || 0);
        setClipEnd(Number(c.t_end) || (Number(c.t_start) + 60));
        setSubs((c.subtitles || []).map((s) => mapSub(s, Number(c.t_start) || 0)));
        setCaption(c.caption || '');
        setTags(Array.isArray(c.tags) ? c.tags : []);
        setDirtyEs(new Set());
      } catch (err) {
        if (alive) {
          setLoadError(err.message);
          toast(err.message, 'err');
        }
      }
    })();
    return () => { alive = false; };
  }, [clipId, toast]);

  const refreshClip = useCallback(async () => {
    try {
      const c = await api.getClip(clipId);
      setClip(c);
    } catch { /* ignore */ }
  }, [clipId]);

  const {
    renderJob,
    publishJob,
    renderActive,
    publishActive,
    pipelineBusy,
    startRender,
    setPublishJob,
  } = useClipPipeline(clipId, { onClipRefresh: refreshClip, toast });

  const hasRendered = !!(clip?.rendered_file_path || renderJob?.rendered_ready);

  useEffect(() => {
    if (!publishOverlay) return;
    if (publishOverlay === 'sched') {
      setPublishOverlay(null);
      return;
    }
    if (publishOverlay === 'wait_render') {
      if (!renderActive && publishActive) setPublishOverlay('now');
      else if (!renderActive && !publishActive) setPublishOverlay(null);
      return;
    }
    if (publishOverlay === 'now' && !publishActive) setPublishOverlay(null);
  }, [publishOverlay, renderActive, publishActive]);

  // Load parent episode in parallel (used only for breadcrumb navigation).
  // Doesn't gate the main editor render.
  useEffect(() => {
    let alive = true;
    if (!clip?.episode_id) return;
    api.getEpisode(clip.episode_id)
      .then((ep) => { if (alive) setEpisode(ep); })
      .catch(() => {});
    return () => { alive = false; };
  }, [clip?.episode_id]);

  useEffect(() => {
    enforceClipBounds();
  }, [clipStart, clipEnd, enforceClipBounds]);

  // Update active sub based on playhead.
  // IMPORTANT: only consider rows that actually display in the CURRENT language.
  // ES and RU are separate interleaved rows (a RU row has no `text`/`text_en`).
  // If we picked the nearest row regardless of language, a RU-only row could
  // become "active" in ES mode and the caption would blank out — the subtitle
  // "disappears / flickers" even though an ES row exists at that moment. Filter
  // to the language's rows so the active caption always has visible text, and so
  // the gap-extension (nextStart) matches the burned-in render's per-mode logic.
  useEffect(() => {
    const langSubs = subs.filter((s) => {
      if (s.kind === 'skip') return false;
      return editLang === 'ru' ? !!s.text_ru : (!!s.text || !!s.text_en);
    });
    const sub = langSubs.find((s, idx) => {
      const nextSub = langSubs[idx + 1];
      const nextStart = nextSub ? nextSub.t : Infinity;
      const effectiveEnd = Math.min(s.t + s.d + 2, nextStart);
      return playhead >= s.t && playhead < effectiveEnd;
    });
    if (sub) setActiveId(sub.id);
  }, [playhead, subs, editLang]);

  const hasSeparateRuRows = useMemo(() => subs.some((s) => !s.text && !!s.text_ru), [subs]);

  // Skip ranges: when playhead enters a skip row, jump to its end
  useEffect(() => {
    if (!playing || !videoRef.current) return;
    const skip = subs.find((s) => s.kind === 'skip' && playhead >= s.t && playhead < s.t + s.d);
    if (skip) {
      try { videoRef.current.currentTime = clipStart + skip.t + skip.d; } catch {}
    }
  }, [playhead, subs, playing, clipStart]);

  if (loadError) {
    return (
      <div className="screen">
        <div className="empty-state">
          <div className="empty-icon">⚠</div>
          <div className="empty-title">не удалось загрузить клип</div>
          <div className="empty-sub">{loadError}</div>
          <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={() => navigate(-1)}>назад</button>
        </div>
      </div>
    );
  }
  if (!clip) {
    return (
      <div className="screen">
        <div className="empty-state">
          <div className="empty-icon">◇</div>
          <div className="empty-title">загружаю клип…</div>
          <div className="empty-sub">если зависло — открой DevTools / Network</div>
        </div>
      </div>
    );
  }

  const active = subs.find((s) => s.id === activeId);
  const totalChars = subs.reduce((a, s) => a + (s.text?.length || 0), 0);
  const deleteIds = selected.size > 0 ? [...selected] : (activeId ? [activeId] : []);

  const updateSubLocal = (id, patch) => setSubs((cur) => cur.map((s) => s.id === id ? { ...s, ...patch } : s));
  const persistSub = (id, patch) => {
    const apiPatch = {};
    if ('text' in patch) apiPatch.text = patch.text;
    if ('text_en' in patch) apiPatch.text_en = patch.text_en;
    if ('t' in patch) apiPatch.word_start_abs = patch.t + clipStart;
    if ('d' in patch || 't' in patch) {
      const s = subs.find((x) => x.id === id);
      if (s) {
        const t = 't' in patch ? patch.t : s.t;
        const d = 'd' in patch ? patch.d : s.d;
        apiPatch.word_end_abs = t + d + clipStart;
      }
    }
    api.updateSubtitle(id, apiPatch).catch((err) => toast(err.message, 'err'));
  };

  const toggleSelect = (id) => setSelected((cur) => {
    const n = new Set(cur);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const merge = async () => {
    const ids = subs.filter((s) => selected.has(s.id)).map((s) => s.id);
    if (ids.length < 2) { toast('выбери две или больше строк', 'info'); return; }
    try {
      const merged = await api.mergeSubtitles(ids);
      await syncSubsFromServer();
      // Merged row's Spanish text was concatenated → its old text_en is stale.
      // Mark the new row dirty so the EN button shows on it.
      setDirtyEs((cur) => {
        const n = new Set(cur);
        ids.forEach((x) => n.delete(x));
        if (merged?.id) n.add(merged.id);
        return n;
      });
      setSelected(new Set());
      setActiveId(merged?.id || null);
      toast(`склеил ${ids.length} строк`, 'ok');
    } catch (err) { toast(err.message, 'err'); }
  };

  const split = async (id) => {
    const s = subs.find((x) => x.id === id);
    if (!s) return;
    const textToSplit = editLang === 'ru' ? (s.text_ru || '') : (s.text || '');
    const off = caret?.id === id ? caret.offset : Math.floor(textToSplit.length / 2);
    const ratio = off / Math.max(1, textToSplit.length);
    const at_abs = s.t + clipStart + s.d * ratio;
    try {
      const result = await api.splitSubtitle(id, { at_abs, split_text_at_char: off, ratio, split_lang: editLang });
      await syncSubsFromServer();
      // Both halves carry stale text_en → mark both dirty.
      setDirtyEs((cur) => {
        const n = new Set(cur);
        if (result?.left?.id) n.add(result.left.id);
        if (result?.right?.id) n.add(result.right.id);
        return n;
      });
      toast('разделил на 2 строки', 'ok');
    } catch (err) { toast(err.message, 'err'); }
  };

  const removeSubs = async (ids) => {
    const list = [...new Set(ids)].filter(Boolean);
    if (!list.length) {
      toast('выбери строку для удаления', 'info');
      return;
    }
    try {
      await Promise.all(list.map((id) => api.deleteSubtitle(id)));
      setSubs((cur) => cur.filter((s) => !list.includes(s.id)));
      setSelected((cur) => {
        const n = new Set(cur);
        list.forEach((id) => n.delete(id));
        return n;
      });
      if (list.includes(activeId)) setActiveId(null);
      toast(list.length === 1 ? 'строка удалена' : `удалено ${list.length} строк`, 'info');
    } catch (err) { toast(err.message, 'err'); }
  };

  const splitTargetId = () => {
    if (selected.size === 1) return [...selected][0];
    if (selected.size > 1 && activeId && selected.has(activeId)) return activeId;
    if (activeId) return activeId;
    return null;
  };

  const splitActive = () => {
    const id = splitTargetId();
    if (!id) {
      toast('выбери строку для разделения', 'info');
      return;
    }
    const s = subs.find((x) => x.id === id);
    if (!s || s.kind === 'skip') {
      toast('пропуск нельзя разделить', 'info');
      return;
    }
    split(id);
  };

  const applySkip = async () => {
    const ids = [...selected].filter((id) => {
      const s = subs.find((x) => x.id === id);
      return s && s.kind !== 'skip';
    });
    if (!ids.length) {
      return addSkipAtPlayhead();
    }
    try {
      await Promise.all(ids.map((id) => api.updateSubtitle(id, { kind: 'skip' })));
      setSubs((cur) => cur.map((s) => (
        ids.includes(s.id) ? { ...s, kind: 'skip', text: '', text_en: '' } : s
      )));
      toast(ids.length === 1 ? 'строка стала пропуском' : `пропуск: ${ids.length} строк`, 'ok');
    } catch (err) { toast(err.message, 'err'); }
  };

  const translateOneEn = async (id) => {
    const updated = await api.translateSubtitleEn(id);
    if (updated?.text_en) {
      updateSubLocal(id, { text_en: updated.text_en });
      setDirtyEs((cur) => {
        if (!cur.has(id)) return cur;
        const n = new Set(cur);
        n.delete(id);
        return n;
      });
      return true;
    }
    return false;
  };

  // Per-row "update translation" — offered on a row whose Spanish was edited.
  const handleRowTranslateEn = async (id) => {
    setTranslatingAll(true);
    try {
      const ok = await translateOneEn(id);
      toast(ok ? 'перевод EN обновлён' : 'перевод пуст', ok ? 'ok' : 'warn');
    } catch (err) {
      toast(err.message, 'err');
    } finally {
      setTranslatingAll(false);
    }
  };

  const translateEn = async () => {
    setTranslatingAll(true);
    try {
      if (selected.size > 0) {
        const ids = subs.filter((s) => selected.has(s.id) && s.kind !== 'skip' && s.text).map((s) => s.id);
        let done = 0;
        for (const id of ids) {
          if (await translateOneEn(id)) done += 1;
        }
        toast(done ? `переведено ${done} строк` : 'нечего переводить', done ? 'ok' : 'info');
        return;
      }

      const pending = subs.filter((s) => (
        s.kind !== 'skip' && s.text && (!String(s.text_en || '').trim() || dirtyEs.has(s.id))
      ));
      if (pending.length > 0) {
        let done = 0;
        for (const s of pending) {
          if (await translateOneEn(s.id)) done += 1;
        }
        toast(done ? `переведено ${done} без EN` : 'перевод пуст', done ? 'ok' : 'warn');
        return;
      }

      const r = await api.translateAllEn(clipId, { force: true });
      if (r?.translated > 0) {
        await syncSubsFromServer();
        setDirtyEs(new Set());
        toast(`все строки переведены заново (${r.translated})`, 'ok');
      } else {
        toast('нечего переводить', 'info');
      }
    } catch (err) { toast(err.message, 'err'); }
    finally { setTranslatingAll(false); }
  };

  const addRow = async () => {
    const sorted = [...subs].sort((a, b) => a.t - b.t);
    const idx = sorted.findIndex((s) => s.id === activeId);
    const cur = idx >= 0 ? sorted[idx] : sorted[sorted.length - 1];
    const next = idx >= 0 && idx + 1 < sorted.length ? sorted[idx + 1] : null;
    const t = cur ? cur.t + cur.d : 0;
    const d = next ? Math.max(0.4, next.t - t) : 2.0;
    try {
      const created = await api.createSubtitle({
        clip_id: clipId,
        word_start_abs: t + clipStart,
        word_end_abs: t + d + clipStart,
        text: '',
      });
      setSubs((arr) => {
        const out = [...arr];
        const insertAt = out.findIndex((s) => s.id === (cur?.id || -1)) + 1;
        out.splice(insertAt, 0, { id: created.id, t, d, text: '' });
        return out;
      });
      setActiveId(created.id);
    } catch (err) { toast(err.message, 'err'); }
  };

  const refreshSubsAfterExtend = async (prev, next) => {
    const expanded = next.t_start < prev.t_start || next.t_end > prev.t_end;
    if (!expanded) return;
    try {
      const r = await api.refreshSubtitles(clipId);
      if (r?.added > 0) {
        await syncSubsFromServer();
        toast(`+ ${r.added} ${r.added === 1 ? 'строка' : r.added < 5 ? 'строки' : 'строк'} субтитров`, 'ok');
      }
    } catch (err) { /* silent */ }
  };

  const syncSubsFromServer = async () => {
    const fresh = await api.getClip(clipId);
    const base = Number(fresh.t_start) || 0;
    setClipStart(base);
    setClipEnd(Number(fresh.t_end) || base + 60);
    setSubs((fresh.subtitles || []).map((s) => mapSub(s, base)));
    return fresh;
  };

  /** Shift segment times when clip.t_start moves (word_start_abs stays fixed). */
  const shiftSubsForClipStart = (oldStart, newStart) => {
    const delta = newStart - oldStart;
    if (Math.abs(delta) < 1e-9) return;
    const newDur = Math.max(1, clipEnd - newStart);
    setSubs((cur) => cur.map((s) => ({ ...s, t: Math.max(0, s.t - delta) })));
    setPlayhead((ph) => Math.max(0, Math.min(newDur, ph - delta)));
  };

  const applyClipStart = async (newStart) => {
    const prevStart = clipStart;
    const x = Math.max(0, Math.min(clipEnd - 1, newStart));
    if (x === prevStart) return;
    shiftSubsForClipStart(prevStart, x);
    setClipStart(x);
    try {
      await api.updateClip(clipId, { t_start: x });
      await refreshSubsAfterExtend({ t_start: prevStart, t_end: clipEnd }, { t_start: x, t_end: clipEnd });
      await syncSubsFromServer();
    } catch (err) {
      setClipStart(prevStart);
      await syncSubsFromServer();
      toast(err.message, 'err');
    }
  };

  const adjustClipStart = (delta) => {
    applyClipStart(clipStart + delta);
  };
  const adjustClipEnd = (delta) => {
    const prevEnd = clipEnd;
    const v = Math.max(clipStart + 1, clipEnd + delta);
    setClipEnd(v);
    api.updateClip(clipId, { t_end: v })
      .then(() => refreshSubsAfterExtend({ t_start: clipStart, t_end: prevEnd }, { t_start: clipStart, t_end: v }))
      .catch((err) => toast(err.message, 'err'));
  };

  const addSkipAtPlayhead = async () => {
    const skipDur = 2;
    const t = Math.max(0, Math.min(clipDur - skipDur, playhead));
    try {
      const created = await api.createSubtitle({
        clip_id: clipId,
        word_start_abs: t + clipStart,
        word_end_abs: Math.min(clipEnd, t + skipDur + clipStart),
        text: '',
        kind: 'skip',
      });
      await syncSubsFromServer();
      setActiveId(created.id);
      toast(`пропуск ${skipDur}с добавлен`, 'ok');
    } catch (err) { toast(err.message, 'err'); }
  };

  const persistCaption = (text) => {
    setCaption(text);
    api.updateClip(clipId, { caption: text }).catch(() => {});
  };
  const persistTags = (next) => {
    setTags(next);
    api.updateClip(clipId, { tags: next }).catch(() => {});
  };

  const renameClip = async (title) => {
    const t = (title || '').trim();
    if (!t || t === clip?.title) return;
    try {
      await api.updateClip(clipId, { title: t });
      setClip((c) => (c ? { ...c, title: t } : c));
      toast('название клипа обновлено', 'ok');
    } catch (err) {
      toast(err.message, 'err');
    }
  };

  const saveClip = async () => {
    try {
      await api.updateClip(clipId, { status: 'draft', caption, tags });
      setClip((c) => (c ? { ...c, status: 'draft', caption, tags } : c));
      toast('сохранено', 'ok');
    } catch (err) {
      toast(err.message, 'err');
    }
  };

  const renderClip = async () => {
    if (renderActive) return;
    try {
      await startRender();
      toast('рендер в очереди', 'ok');
    } catch (err) {
      toast(err.message, 'err');
    }
  };

  const buildPublishTargets = (kinds) => {
    const targets = [];
    if (kinds.ig_reels) {
      targets.push(kinds.ig_stories ? 'ig_reels_with_story' : 'ig_reels');
    } else if (kinds.ig_stories) {
      targets.push('ig_stories');
    }
    if (kinds.ig_feed) targets.push('ig_feed');
    if (kinds.yt_shorts) targets.push('yt_shorts');
    if (kinds.vk_clip) targets.push('vk_clip');
    if (kinds.telegram) targets.push('telegram');
    return targets;
  };

  const togglePostKind = (key) => {
    setPostKinds((prev) => {
      const on = !prev[key];
      if (key === 'ig_reels' && on) {
        return { ...prev, ig_reels: true, ig_stories: true };
      }
      return { ...prev, [key]: on };
    });
  };

  const publish = async (target) => {
    const chosen = buildPublishTargets(postKinds);
    if (target === 'ig' && !chosen.some((t) => t.startsWith('ig_'))) {
      chosen.unshift(postKinds.ig_stories ? 'ig_reels_with_story' : 'ig_reels');
    }
    if (target === 'tg' && !chosen.includes('telegram')) chosen.push('telegram');
    const final = Array.from(new Set(chosen));
    if (!final.length) { toast('выбери хотя бы один таргет', 'warn'); return; }
    const at = postWhen === 'now'
      ? new Date().toISOString()
      : new Date(`${schedDate}T${schedTime}:00-05:00`).toISOString();
    const needsRender = !hasRendered;
    const willStartRender = needsRender && !renderActive;

    try {
      if (willStartRender) await startRender();
      const job = await api.schedulePublish({ clip_id: clipId, scheduled_at: at, targets: final, caption });
      setPublishJob(job);

      if (willStartRender || renderActive) {
        setPublishOverlay('wait_render');
        toast(
          postWhen === 'sched'
            ? `запланировано · ${schedDate} ${schedTime} — сначала рендер`
            : 'сначала рендер, затем публикация',
          'ok',
        );
        if (postWhen === 'sched') navigate('/schedule');
      } else if (postWhen === 'now') {
        setPublishOverlay('now');
        toast('публикация запущена', 'ok');
      } else {
        toast(`запланировано · ${schedDate} ${schedTime}`, 'ok');
        navigate('/schedule');
      }
    } catch (err) {
      toast(err.message, 'err');
      setPublishOverlay(null);
    }
  };

  const videoUrl = clip && api.videoUrl(clip.episode_id);

  return (
    <div className="screen editor-screen">
      <div className="editor-layout">
        {/* LEFT — full-height 9:16 preview + transport */}
        <aside className="editor-aside">
          <VideoPreview
            videoRef={videoRef}
            videoUrl={videoUrl}
            activeSub={active}
            editLang={editLang}
            settings={settings}
            playing={playing}
            speed={speed}
            playhead={playhead}
            clipStart={clipStart}
            clipEnd={clipEnd}
            clipDur={clipDur}
            onTogglePlay={() => {
              const v = videoRef.current;
              if (!v) return;
              if (playing) {
                v.pause();
                return;
              }
              if (playhead >= clipDur - 0.05) seekTo(0);
              v.play().catch(() => {});
            }}
            onSeek={seekTo}
            onTimeUpdate={enforceClipBounds}
            onSeeked={enforceClipBounds}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onLoaded={() => {
              if (!videoRef.current) return;
              try { videoRef.current.currentTime = clipStart; } catch {}
            }}
            onError={(e) => {
              const err = e.target?.error;
              const msg = err?.code === 1 ? 'aborted'
                : err?.code === 2 ? 'network error'
                : err?.code === 3 ? 'cannot decode (codec?)'
                : err?.code === 4 ? 'source not supported'
                : `error ${err?.code}`;
              setVideoError(msg);
              toast(`видео: ${msg}`, 'err');
            }}
            error={videoError}
            onSpeedChange={(s) => {
              setSpeed(s);
              if (videoRef.current) videoRef.current.playbackRate = s;
            }}
          />

        </aside>

        <div className="editor-right">
          <div className="breadcrumb breadcrumb-oneline">
            <button type="button" className="back" onClick={() => navigate(`/episode/${episode?.id || clip.episode_id}`)}>← транскрипт</button>
            <span className="crumb-sep">/</span>
            <span className="crumb-current" title={episode?.title}>{episode?.title || '…'}</span>
            <span className="crumb-sep">/</span>
            <EditableClipTitle value={clip.title} onSave={renameClip} />
            <span className="chip chip-soft chip-compact">{fmtClock(clipStart)}–{fmtClock(clipEnd)} · {fmtDur(clipDur)}</span>
          </div>

          {/* RIGHT — clip bounds + subtitles + publish */}
          <section className="editor-main">
          <div className="clip-boundary ed-card clip-boundary-start">
            <div className="ed-card-head">
              <div className="ed-card-title">Начало клипа</div>
              <span className="ed-card-sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(clipStart)} · {fmtDur(clipDur)}</span>
            </div>
            <ClipTimeRow label="начало" value={clipStart}
                         onChange={(v) => applyClipStart(v)}
                         onStep={adjustClipStart} />
            <div className="ed-card-hint">±1 с — субтитры пересчитываются относительно нового начала</div>
          </div>

          <div className="sub-sticky-head">
            <div className="editor-toolbar editor-toolbar-compact">
              <h2 className="hand-h2">Субтитры</h2>
              <span className="muted">{subs.length} строк · {totalChars} знаков</span>
              {selected.size > 0 && (
                <span className="chip chip-soft">выбрано: {selected.size}</span>
              )}
              <button
                type="button"
                className="btn btn-outline btn-compact"
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', borderColor: 'rgba(201, 122, 43, 0.4)', color: 'var(--accent)' }}
                onClick={() => window.open('/settings', '_blank')}
                title="Настройки стиля субтитров (открыть в новой вкладке)"
              >
                {Icons.settings} настройки стиля
              </button>
            </div>

            <div className="sub-edit-panel" onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 6, padding: 2, marginRight: 8 }}>
                <button
                  type="button"
                  className={`btn btn-compact ${editLang === 'es' ? 'btn-dark' : 'btn-ghost'}`}
                  onClick={() => setEditLang('es')}
                >
                  ES + EN
                </button>
                <button
                  type="button"
                  className={`btn btn-compact ${editLang === 'ru' ? 'btn-dark' : 'btn-ghost'}`}
                  onClick={() => setEditLang('ru')}
                >
                  RU (VK)
                </button>
              </div>

              <button type="button" className="btn btn-dark btn-compact" onClick={addRow} title="новая строка после активной">
                {Icons.plus} строка
              </button>
              <button
                type="button"
                className={'btn btn-outline btn-compact' + (selected.size < 2 ? ' btn-disabled' : '')}
                onClick={merge}
                disabled={selected.size < 2}
                title="склеить отмеченные (2+)"
              >
                {Icons.merge} склеить{selected.size >= 2 ? ` ${selected.size}` : ''}
              </button>
              <button type="button" className="btn btn-outline btn-compact" onClick={splitActive} title="разделить по курсору">
                {Icons.scissors} разделить
              </button>
              <button
                type="button"
                className="btn btn-outline btn-compact"
                onClick={applySkip}
                title={selected.size > 0 ? 'пропуск для выбранных' : 'пропуск 2с на плеере'}
              >
                ⏵⏵{selected.size > 0 ? ` ${selected.size}` : ''}
              </button>
              {editLang === 'es' && (
                <button
                  type="button"
                  className="btn btn-outline btn-compact"
                  onClick={translateEn}
                  disabled={translatingAll}
                  title={selected.size > 0 ? 'EN для выбранных' : 'EN: сначала пустые/устаревшие, затем все'}
                >
                  {translatingAll ? '…' : 'EN'}{selected.size > 0 ? ` ${selected.size}` : ' всё'}
                </button>
              )}
              <button
                type="button"
                className={'btn btn-outline btn-compact btn-danger-outline' + (deleteIds.length ? '' : ' btn-disabled')}
                onClick={() => removeSubs(deleteIds)}
                disabled={!deleteIds.length}
                title="удалить"
              >
                {Icons.trash}{deleteIds.length > 1 ? ` ${deleteIds.length}` : ''}
              </button>
            </div>

            <div className="sub-table-head">
              <span />
              <span>начало</span>
              <span>конец</span>
              <span>{editLang === 'es' ? 'texto · ES / EN' : 'текст · RU'}</span>
            </div>
          </div>

          <div className="sub-list">
            {subs.filter((s) => {
              if (s.kind === 'skip') return true;
              if (editLang === 'es') return !!s.text || !!s.text_en || (!s.text_ru && !s.text);
              if (editLang === 'ru') {
                if (hasSeparateRuRows) return !!s.text_ru;
                return !!s.text || !!s.text_en || !!s.text_ru || (!s.text_ru && !s.text);
              }
              return true;
            }).map((s) => (
              <SubRow key={s.id} s={s}
                      editLang={editLang}
                      isActive={s.id === activeId}
                      isSelected={selected.has(s.id)}
                      isDirty={dirtyEs.has(s.id)}
                      translating={translatingAll}
                      playhead={playhead}
                      onTranslateEn={() => handleRowTranslateEn(s.id)}
                      onSelect={() => { setActiveId(s.id); seekTo(s.t); }}
                      onToggleCheck={() => toggleSelect(s.id)}
                      onChangeEs={(text) => {
                        if (text === s.text) return;
                        updateSubLocal(s.id, { text });
                        persistSub(s.id, { text });
                        setDirtyEs((cur) => {
                          if (cur.has(s.id)) return cur;
                          const n = new Set(cur); n.add(s.id); return n;
                        });
                      }}
                      onChangeRu={(text) => {
                        if (text === (s.text_ru || '')) return;
                        updateSubLocal(s.id, { text_ru: text });
                        persistSub(s.id, { text_ru: text });
                      }}
                      onChangeText_en={(text_en) => {
                        if (text_en === (s.text_en || '')) return;
                        updateSubLocal(s.id, { text_en });
                        persistSub(s.id, { text_en });
                      }}
                      onChangeStart={(t) => { updateSubLocal(s.id, { t }); persistSub(s.id, { t }); }}
                      onChangeEnd={(end) => { const d = Math.max(0.3, end - s.t); updateSubLocal(s.id, { d }); persistSub(s.id, { d }); }}
                      onCaret={(offset) => setCaret({ id: s.id, offset })} />
            ))}
            {subs.length === 0 && <div className="empty">субтитры ещё генерируются…</div>}
          </div>

          <div className="clip-boundary ed-card clip-boundary-end">
            <div className="ed-card-head">
              <div className="ed-card-title">Конец клипа</div>
              <span className="ed-card-sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(clipEnd)}</span>
            </div>
            <ClipTimeRow label="конец" value={clipEnd}
                         onChange={(v) => {
                           const prev = clipEnd;
                           const x = Math.max(clipStart + 1, v);
                           setClipEnd(x);
                           api.updateClip(clipId, { t_end: x })
                             .then(() => refreshSubsAfterExtend({ t_start: clipStart, t_end: prev }, { t_start: clipStart, t_end: x }))
                             .catch((err) => toast(err.message, 'err'));
                         }}
                         onStep={adjustClipEnd} />
            <div className="ed-card-hint">±1 с — при расширении подтянутся новые строки из транскрипта</div>
          </div>

          <PublishPanel
            clip={clip}
            caption={caption} setCaption={persistCaption}
            tags={tags} setTags={persistTags}
            topics={topics}
            postKinds={postKinds} togglePostKind={togglePostKind}
            postWhen={postWhen} setPostWhen={setPostWhen}
            schedDate={schedDate} setSchedDate={setSchedDate}
            schedTime={schedTime} setSchedTime={setSchedTime}
            generatingCaption={generatingCaption}
            onGenerateCaption={async () => {
              setGeneratingCaption(true);
              try {
                const r = await api.generateCaption(clipId);
                if (r?.caption) {
                  setCaption(r.caption);
                  if (Array.isArray(r.tags) && r.tags.length) setTags(r.tags);
                  toast('описание сгенерировано', 'ok');
                } else {
                  toast('пустой ответ от DeepSeek', 'warn');
                }
              } catch (err) {
                toast(err.message || 'ошибка генерации', 'err');
              } finally { setGeneratingCaption(false); }
            }}
            onSave={saveClip}
            onRender={renderClip}
            renderJob={renderJob}
            renderActive={renderActive}
            publishJob={publishJob}
            publishActive={publishActive}
            hasRendered={hasRendered}
            onPublish={publish}
            publishBusy={!!publishOverlay || publishActive} />
          </section>
        </div>
      </div>

      {publishOverlay && (
        <PipelineOverlay
          mode={publishOverlay}
          renderJob={renderJob}
          publishJob={publishJob}
          renderActive={renderActive}
          publishActive={publishActive}
          hasRendered={hasRendered}
        />
      )}
    </div>
  );
}

// ---- 9:16 video preview -------------------------------------------------

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function VideoPreview({ videoRef, videoUrl, activeSub, editLang, settings, playing, speed, playhead, clipStart, clipEnd, clipDur, onTogglePlay, onSeek, onTimeUpdate, onSeeked, onPlay, onPause, onLoaded, onError, error, onSpeedChange }) {
  const screenRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(0);

  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const sync = () => { applyCaptionPreviewScale(el); setBoxHeight(el.clientHeight); };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="ed-video-wrap">
      <div className="video-preview">
        <div className="video-preview-screen" ref={screenRef}>
          {videoUrl ? (
            <video ref={videoRef} src={videoUrl}
                   preload="auto"
                   playsInline
                   controls
                   onPlay={onPlay} onPause={onPause}
                   onTimeUpdate={onTimeUpdate}
                   onSeeking={onSeeked}
                   onSeeked={onSeeked}
                   onLoadedMetadata={onLoaded}
                   onError={onError}
                   className="video-preview-el" />
          ) : (
            <div className="video-preview-placeholder">видео ещё не готово</div>
          )}
          {error && (
            <div className="video-preview-error">⚠ {error}</div>
          )}
          <div className="speed-badge">{speed.toFixed(2)}×</div>
          
          {settings ? (
            <>
              {editLang === 'ru' ? (
                !!activeSub?.text_ru && (
                  <div className="preview-subtitle-line line-ru" style={previewLineStyle(settings.ru, { boxHeight })}>
                    <span style={previewBoxStyle(settings.ru)}>{activeSub.text_ru}</span>
                  </div>
                )
              ) : (
                <>
                  {!!activeSub?.text && (
                    <div className="preview-subtitle-line line-es" style={previewLineStyle(settings.es, { boxHeight })}>
                      <span style={previewBoxStyle(settings.es)}>{activeSub.text}</span>
                    </div>
                  )}
                  {!!activeSub?.text_en && (
                    <div className="preview-subtitle-line line-en" style={previewLineStyle(settings.en, { boxHeight, posY: resolveEnPosY(settings), anchor: 'top' })}>
                      <span style={previewBoxStyle(settings.en)}>{activeSub.text_en}</span>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            (editLang === 'ru' ? !!activeSub?.text_ru : (!!activeSub?.text || !!activeSub?.text_en)) && (
              <div className="video-cap-bi video-cap-bi-overlay">
                {editLang === 'ru' ? (
                  <div className="video-cap-es">{activeSub?.text_ru}</div>
                ) : (
                  <>
                    {activeSub?.text && <div className="video-cap-es">{activeSub.text}</div>}
                    {activeSub?.text_en && <div className="video-cap-en">{activeSub.text_en}</div>}
                  </>
                )}
              </div>
            )
          )}
        </div>
      </div>

      <div className="scrub" onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        onSeek(f * clipDur);
      }}>
        <div className="scrub-fill" style={{ width: (playhead / clipDur) * 100 + '%' }} />
        <div className="scrub-knob" style={{ left: (playhead / clipDur) * 100 + '%' }} />
      </div>
      <div className="scrub-times">
        <span>{fmtT(playhead)}</span>
        <span>/</span>
        <span>{fmtT(clipDur)}</span>
      </div>

      <div className="transport">
        <button type="button" className="iconbtn" title="в начало клипа" onClick={() => onSeek(0)}>{Icons.prev}</button>
        <button
          type="button"
          className="iconbtn transport-seek-btn"
          title="назад 3 с"
          onClick={() => onSeek(Math.max(0, playhead - 3))}
        >
          −3с
        </button>
        <button type="button" className="iconbtn iconbtn-primary big-play" title={playing ? 'пауза' : 'воспроизведение'} onClick={onTogglePlay}>{playing ? Icons.pause : Icons.play}</button>
        <button
          type="button"
          className="iconbtn transport-seek-btn"
          title="вперёд 3 с"
          onClick={() => onSeek(Math.min(clipDur, playhead + 3))}
        >
          +3с
        </button>
        <TransportSpeedMenu speed={speed} onSpeedChange={onSpeedChange} />
      </div>
    </div>
  );
}

function TransportHoverMenu({ label, title, ariaLabel, onTrigger, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={'transport-hover-menu' + (open ? ' is-open' : '')}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="iconbtn transport-hover-trigger"
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={onTrigger}
      >
        <span className="transport-hover-trigger-val">{label}</span>
        <span className="transport-hover-caret" aria-hidden>▾</span>
      </button>
      <div className="transport-hover-popup" role="listbox" aria-label={ariaLabel}>
        {children}
      </div>
    </div>
  );
}

function TransportSpeedMenu({ speed, onSpeedChange }) {
  const label = Number.isInteger(speed) ? `${speed}` : String(speed);
  return (
    <TransportHoverMenu
      label={`${label}×`}
      title="скорость воспроизведения"
      ariaLabel="скорость"
    >
      {PLAYBACK_SPEEDS.map((s) => (
        <button
          key={s}
          type="button"
          role="option"
          aria-selected={s === speed}
          className={'transport-hover-opt' + (s === speed ? ' is-active' : '')}
          onClick={() => onSpeedChange(s)}
        >
          {s}×
        </button>
      ))}
    </TransportHoverMenu>
  );
}

// ---- Sub row -----------------------------------------------------------

const SubRow = React.memo(function SubRow({ s, editLang, isActive, isSelected, isDirty, translating, playhead, onSelect, onToggleCheck, onChangeEs, onChangeRu, onChangeText_en, onChangeStart, onChangeEnd, onCaret, onTranslateEn }) {
  const end = s.t + s.d;
  const isPlaying = playhead >= s.t && playhead < end;
  const isSkip = s.kind === 'skip';

  return (
    <div className={'sub-row' + (isActive ? ' sub-active' : '') + (isSelected ? ' sub-selected' : '') + (isPlaying ? ' sub-playing' : '') + (isSkip ? ' sub-skip' : '') + (isDirty && editLang === 'es' ? ' sub-dirty' : '')}
         onClick={onSelect}>
      <label className="check" onClick={(e) => { e.stopPropagation(); onToggleCheck(); }}>
        <span className={'check-box ' + (isSelected ? 'check-on' : '')}>{isSelected ? '✓' : ''}</span>
      </label>

      <TimeInput value={s.t} onChange={onChangeStart} />
      <TimeInput value={end} onChange={onChangeEnd} />

      <div className="sub-text-cell">
        {isSkip ? (
          <div className="sub-text-skip">
            ⏵⏵ пропуск · {s.d.toFixed(1)}с (не рендерится, плеер прыгает дальше)
          </div>
        ) : editLang === 'ru' ? (
          <>
            <EditableSubText text={s.text_ru || ''} className="sub-text es" placeholder="текст (RU)…"
                             onChange={onChangeRu} onFocus={onSelect} />
            <span className="sub-charcount">{(s.text_ru || '').length}ru</span>
          </>
        ) : (
          <>
            <EditableSubText text={s.text || ''} className="sub-text es" placeholder="пустая строка — клик для ввода"
                             onChange={onChangeEs} onCaret={onCaret} onFocus={onSelect} />
            <EditableSubText text={s.text_en || ''} className={'sub-text en sub-text-en' + (isDirty ? ' sub-text-en-stale' : '')} placeholder="перевод (EN)…"
                             onChange={onChangeText_en} onFocus={onSelect} />
            <div className="sub-text-meta">
              <span className="sub-charcount">{(s.text || '').length}{(s.text_en) ? ` · ${s.text_en.length}en` : ''}{isDirty ? ' · ⚠ EN устарел' : ''}</span>
              {isDirty && (
                <button
                  type="button"
                  className="btn btn-outline btn-xs sub-translate-en"
                  disabled={translating}
                  title="обновить английский перевод этой строки"
                  onClick={(e) => { e.stopPropagation(); onTranslateEn && onTranslateEn(); }}
                >
                  ↻ обновить EN
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}, (prev, next) => {
  if (prev.s !== next.s || prev.editLang !== next.editLang || prev.isActive !== next.isActive || prev.isSelected !== next.isSelected || prev.isDirty !== next.isDirty || prev.translating !== next.translating) return false;
  const prevIsPlaying = prev.playhead >= prev.s.t && prev.playhead < (prev.s.t + prev.s.d);
  const nextIsPlaying = next.playhead >= next.s.t && next.playhead < (next.s.t + next.s.d);
  if (prevIsPlaying !== nextIsPlaying) return false;
  return true;
});

function EditableSubText({ text, onChange, onCaret, onFocus, className = 'sub-text es', placeholder = '' }) {
  const ref = useRef(null);
  const focused = useRef(false);

  useEffect(() => {
    if (ref.current && !focused.current && ref.current.innerText !== text) {
      ref.current.innerText = text || '';
    }
  }, [text]);

  useEffect(() => {
    if (ref.current) ref.current.innerText = text || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCaret = () => {
    const sel = window.getSelection();
    if (!sel || !ref.current || sel.rangeCount === 0) return 0;
    const r = sel.getRangeAt(0).cloneRange();
    r.setStart(ref.current, 0);
    return r.toString().length;
  };

  return (
    <div
      ref={ref}
      className={className}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      data-placeholder={placeholder}
      onFocus={() => { focused.current = true; onFocus && onFocus(); }}
      onBlur={(e) => { focused.current = false; onChange(e.target.innerText); }}
      onInput={() => onCaret && onCaret(getCaret())}
      onKeyUp={() => onCaret && onCaret(getCaret())}
      onMouseUp={() => onCaret && onCaret(getCaret())}
      onKeyDown={(e) => {
        // Enter inserts a real newline (so subtitles can be multi-line — \N in ASS).
        // Ctrl/Cmd+Enter commits (blurs).
        // Escape commits without further changes.
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.target.blur(); return; }
        if (e.key === 'Enter') {
          e.preventDefault();
          // Insert plain newline at caret instead of <br>/<div> which mess up innerText
          document.execCommand('insertText', false, '\n');
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); e.target.blur(); }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ whiteSpace: 'pre-wrap' }}
    />
  );
}

// Inline-editable clip title in the breadcrumb — click to rename.
function EditableClipTitle({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  if (editing) {
    return (
      <input
        className="crumb-title-input"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onSave(draft); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
      />
    );
  }
  return (
    <span
      className="crumb-current crumb-strong crumb-editable"
      title="кликни, чтобы переименовать клип"
      onClick={() => setEditing(true)}
    >
      {value || 'без названия'} <span className="crumb-edit-pencil">✎</span>
    </span>
  );
}

function TimeInput({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  return editing ? (
    <input className="time-input" autoFocus
           value={draft}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={() => {
             const parsed = parseSubTime(draft);
             if (parsed != null) onChange(parsed);
             setEditing(false);
           }}
           onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false); }} />
  ) : (
    <button className="time-cell" onClick={(e) => { e.stopPropagation(); setDraft(fmtT(value)); setEditing(true); }}>{fmtT(value)}</button>
  );
}

// ---- Clip time controls ------------------------------------------------

function ClipTimeRow({ label, value, onChange, onStep }) {
  const [draft, setDraft] = useState(fmtClock(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(fmtClock(value)); }, [value, focused]);

  const commit = () => {
    const parsed = parseClock(draft);
    if (parsed != null) onChange(parsed);
    else setDraft(fmtClock(value));
    setFocused(false);
  };

  return (
    <div className="trim-row">
      <span className="trim-label">{label}</span>
      <button className="trim-step" onClick={() => onStep(-1)} title="−1с">−</button>
      <input className="trim-time"
             value={draft}
             onChange={(e) => setDraft(e.target.value)}
             onFocus={(e) => { setFocused(true); e.target.select(); }}
             onBlur={commit}
             onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(fmtClock(value)); e.target.blur(); } }} />
      <button className="trim-step" onClick={() => onStep(+1)} title="+1с">+</button>
    </div>
  );
}

// ---- Publish panel -----------------------------------------------------

function PublishPanel({
  clip, caption, setCaption, tags, setTags, topics,
  postKinds, togglePostKind, postWhen, setPostWhen,
  schedDate, setSchedDate, schedTime, setSchedTime,
  onSave, onRender, renderJob, renderActive, publishJob, publishActive, hasRendered,
  onPublish, publishBusy,
  onGenerateCaption, generatingCaption,
}) {
  const [newTag, setNewTag] = useState('');

  const addTag = () => {
    const t = newTag.trim().toLowerCase().replace(/^#?/, '');
    if (t.length < 2 || tags.includes(t)) return;
    setTags([...tags, t]); setNewTag('');
  };

  return (
    <div className="publish-panel">
      <section className="publish-caption">
        <div className="publish-row">
          <h3 className="hand-h3">Descripción</h3>
          <span className="chip chip-hue-amber">✨ ИИ-черновик</span>
          <span className="muted small">solo en español</span>
          <div className="grow" />
          <button
            className="btn btn-outline btn-sm"
            onClick={onGenerateCaption}
            disabled={generatingCaption}
            title="сгенерировать описание из субтитров через DeepSeek"
          >
            {generatingCaption ? (<><span className="spinner" /> генерирую…</>) : '✨ сгенерировать'}
          </button>
          <span className="muted small">{(caption || '').length} / 2200</span>
        </div>

        <textarea className="caption-area" value={caption || ''} onChange={(e) => setCaption(e.target.value)} rows={5} />

        <div className="publish-row">
          <span className="muted small">этикетки:</span>
          {tags.map((t) => {
            const topic = findTopic(topics, t);
            return (
              <span key={t} className={'chip chip-hue-' + topic.hue + ' chip-removable'}>
                #{topic.label}
                <button type="button" className="chip-x" onClick={() => setTags(tags.filter((x) => x !== t))}>×</button>
              </span>
            );
          })}
          <span className="chip chip-dashed">
            <input className="chip-input" placeholder="+ этикетка" value={newTag}
                   onChange={(e) => setNewTag(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }} />
          </span>
        </div>
      </section>

      <div className="publish-actions">
        <button type="button" className="btn btn-outline" onClick={onSave}>сохранить</button>
        <button type="button" className="btn btn-dark" onClick={onRender} disabled={renderActive}>
          {renderActive ? (<><span className="spinner" /> {renderStatusLabel(renderJob?.status)}</>) : (<>⟳ рендер</>)}
        </button>
        {!hasRendered && !renderActive && (
          <span className="muted small publish-actions-hint">рендер создаст 9:16 — публикация может ждать его в очереди</span>
        )}
      </div>

      {(renderActive || publishJob || renderJob?.status === 'failed') && (
        <ClipPipelineCard
          renderJob={renderJob}
          publishJob={publishJob}
          renderActive={renderActive}
          publishActive={publishActive}
          hasRendered={hasRendered}
        />
      )}

      <section className="publish-settings">
        <div className="publish-settings-head">
          <h3 className="hand-h3">Публикация</h3>
          <div className="acct-row acct-row-inline">
            <span className="acct-mark">{Icons.instagram}</span>
            <div>
              <div className="acct-name">@dosmundos_peru</div>
              <div className="acct-handle">Instagram · Dos Mundos</div>
            </div>
          </div>
        </div>

        <div className="kind-list kind-list-grid">
          {[
            ['ig_reels',   '◢ Instagram · Reels (9:16)'],
            ['ig_stories', postKinds.ig_reels ? '◢ Instagram · Истории (репост Reels)' : '◢ Instagram · Истории (24 ч)'],
            ['ig_feed',    '◢ Instagram · Feed'],
            ['yt_shorts',  '▶ YouTube · Shorts'],
            ['vk_clip',    '⬢ VK · Клип (Russian)'],
            ['telegram',   '✈ Канал Telegram'],
          ].map(([k, l]) => (
            <label key={k} className="kind-row" onClick={() => togglePostKind(k)}>
              <span className={'check-box ' + (postKinds[k] ? 'check-on' : '')}>{postKinds[k] ? '✓' : ''}</span>
              <span>{l}</span>
            </label>
          ))}
        </div>

        <div className="when-row">
          <span className="muted small">когда:</span>
          <button className={'pill ' + (postWhen === 'now' ? 'pill-on' : '')} onClick={() => setPostWhen('now')}>сейчас</button>
          <button className={'pill ' + (postWhen === 'sched' ? 'pill-on' : '')} onClick={() => setPostWhen('sched')}>запланировать</button>
        </div>

        {postWhen === 'sched' && (
          <div className="sched-picker">
            <div className="sched-picker-row">
              <label className="sched-picker-field">
                <span className="sched-picker-label">дата</span>
                <input type="date" className="sched-picker-input" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} />
              </label>
              <label className="sched-picker-field">
                <span className="sched-picker-label">время</span>
                <input type="time" className="sched-picker-input" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} />
              </label>
            </div>
            <div className="sched-picker-note">
              <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6" /><path d="M8 4v4l2 2" strokeLinecap="round" /></svg>
              <span>по перуанскому времени <b>UTC−5</b> · Сейчас в Лиме: <PeruClock /></span>
            </div>
          </div>
        )}

        <button type="button" className={'btn btn-publish btn-full publish-cta ' + (publishBusy ? 'is-busy' : '')}
                onClick={() => onPublish()} disabled={publishBusy}>
          {publishBusy
            ? <><span className="spinner" /> {publishActive ? 'публикую…' : renderActive ? 'рендер…' : '…'}</>
            : (postWhen === 'now'
              ? (hasRendered ? 'опубликовать сейчас' : 'рендер + опубликовать')
              : (hasRendered ? 'добавить в очередь' : 'рендер + в очередь'))}
        </button>
        {!hasRendered && postWhen === 'sched' && (
          <p className="muted small pipeline-hint">
            Публикация встанет в очередь и запустится после готового рендера (и в выбранное время, если оно позже).
          </p>
        )}
      </section>
    </div>
  );
}

function ClipPipelineCard({ renderJob, publishJob, renderActive, publishActive, hasRendered }) {
  const waitingRender = publishJob?.status === 'scheduled' && !hasRendered;
  return (
    <div className="clip-pipeline-card">
      <div className="clip-pipeline-row">
        <span className="clip-pipeline-step">1</span>
        <div className="clip-pipeline-body">
          <div className="clip-pipeline-title">Рендер 9:16</div>
          <div className={'clip-pipeline-status' + (renderJob?.status === 'failed' ? ' is-err' : renderActive ? ' is-active' : renderJob?.status === 'done' ? ' is-ok' : '')}>
            {renderActive && <span className="spinner" />}
            {renderStatusLabel(renderJob?.status || (hasRendered ? 'done' : '—'))}
            {renderJob?.duration_seconds != null && renderJob.status === 'done' && (
              <span className="muted small"> · {Number(renderJob.duration_seconds).toFixed(0)} с</span>
            )}
          </div>
          {renderJob?.error_message && renderJob.status === 'failed' && (
            <div className="clip-pipeline-err">{renderJob.error_message}</div>
          )}
        </div>
      </div>
      {publishJob && (
        <div className="clip-pipeline-row">
          <span className="clip-pipeline-step">2</span>
          <div className="clip-pipeline-body">
            <div className="clip-pipeline-title">Публикация</div>
            <div className={'clip-pipeline-status' + (publishActive ? ' is-active' : publishJob.status === 'published' ? ' is-ok' : publishJob.status === 'failed' ? ' is-err' : '')}>
              {publishActive && <span className="spinner" />}
              {publishStatusLabel(publishJob.status, { waitingRender })}
            </div>
            {waitingRender && (
              <div className="muted small">запустится автоматически, когда рендер будет готов</div>
            )}
            {publishJob.error_message && publishJob.status === 'failed' && (
              <div className="clip-pipeline-err">{publishJob.error_message}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineOverlay({ mode, renderJob, publishJob, renderActive, publishActive, hasRendered }) {
  const waitingRender = !hasRendered && (renderActive || mode === 'wait_render');
  let title = 'публикую…';
  let sub = 'загружаю в соцсети';
  if (waitingRender && renderActive) {
    title = 'рендер 9:16…';
    sub = 'вшиваю субтитры · после этого — публикация из очереди';
  } else if (waitingRender && !renderActive) {
    title = 'рендер готов · публикация…';
    sub = 'запускаю загрузку';
  } else if (mode === 'sched') {
    title = 'добавляю в очередь…';
    sub = '';
  }

  return (
    <div className="publish-overlay">
      <div className="publish-overlay-card">
        <div className="spinner spinner-big" />
        <div className="publish-overlay-title">{title}</div>
        {sub && <div className="publish-overlay-sub">{sub}</div>}
        <ClipPipelineCard
          renderJob={renderJob}
          publishJob={publishJob}
          renderActive={renderActive}
          publishActive={publishActive}
          hasRendered={hasRendered}
        />
      </div>
    </div>
  );
}

function PeruClock() {
  const [now, setNow] = useState(() => peruTime(new Date()));
  useEffect(() => {
    const id = setInterval(() => setNow(peruTime(new Date())), 1000);
    return () => clearInterval(id);
  }, []);
  return <strong>{now}</strong>;
}
function peruTime(d) {
  const peru = new Date(d.getTime() + (d.getTimezoneOffset() - 5 * 60) * 60 * 1000);
  return `${String(peru.getHours()).padStart(2,'0')}:${String(peru.getMinutes()).padStart(2,'0')}:${String(peru.getSeconds()).padStart(2,'0')}`;
}
